import { useQuery } from '@tanstack/react-query';
import { FC, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { _getActivityDay } from '@/api/gamma-team/queries';
import type {
  ActivityDayDeal,
  ActivityDayEntry,
  ActivityDayResponse,
} from '@/api/gamma-team/types';
import ChevronLeft from '@/assets/svg/ui-elements/chevron-left.svg';
import ChevronRight from '@/assets/svg/ui-elements/chevron-right.svg';
import {
  Alert,
  Button,
  Drawer,
  EmptyState,
  IconButton,
  TextButton,
  theme,
  Typography,
} from '@/design-system';

import { UNNAMED_CONTACT } from '../../address-book/modules/address-book-board/utils/contact-presentation';
import { ActivityEntry, DELETED_CONTACT_LABEL } from './contact-activity';
import { toContactHistoryHref } from './contact-href';
import { DealRow } from './member-activity';
import {
  ContactList,
  DrawerBody,
  QuietLine,
  Section,
  SectionActions,
  SectionHeader,
} from './styled';
import { memberName } from './team-sorting';
import { addIsoDays, formatDayLabel } from './team-trends';

const DRAWER_WIDTH = 720;

export const dayTitle = (day: string): string => `Activity on ${formatDayLabel(day)}`;

interface ActorGroup {
  userId: string;
  name: string;
  items: ActivityDayEntry[];
  deals: ActivityDayDeal[];
}

const plural = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

export const groupHeading = (group: ActorGroup): string =>
  [
    group.name,
    group.items.length > 0 ? plural(group.items.length, 'entry', 'entries') : null,
    group.deals.length > 0 ? plural(group.deals.length, 'deal move', 'deal moves') : null,
  ]
    .filter(Boolean)
    .join(' · ');

const groupByActor = (
  entries: readonly ActivityDayEntry[],
  deals: readonly ActivityDayDeal[],
): ActorGroup[] => {
  const groups = new Map<string, ActorGroup>();
  const groupOf = (actor: ActivityDayEntry['actor']): ActorGroup => {
    const group = groups.get(actor.userId) ?? {
      userId: actor.userId,
      name: memberName(actor),
      items: [],
      deals: [],
    };
    groups.set(actor.userId, group);
    return group;
  };
  for (const item of entries) groupOf(item.actor).items.push(item);
  for (const deal of deals) groupOf(deal.actor).deals.push(deal);
  return [...groups.values()].sort(
    (a, b) =>
      b.items.length + b.deals.length - (a.items.length + a.deals.length) ||
      a.name.localeCompare(b.name),
  );
};

const contactLabel = (item: ActivityDayEntry): string =>
  item.contact.name?.trim() || (item.contact.deleted ? DELETED_CONTACT_LABEL : UNNAMED_CONTACT);

const DayContent: FC<{ data: ActivityDayResponse; onOpenMember: (_userId: string) => void }> = ({
  data,
  onOpenMember,
}) => {
  const groups = useMemo(() => groupByActor(data.entries, data.deals), [data.entries, data.deals]);

  if (groups.length === 0) {
    return <EmptyState variant="tertiary" role="status" title="Nothing was logged on this day." />;
  }

  return (
    <>
      {data.truncated && (
        <Alert
          variant="warning"
          description="Only the most recent 500 entries of this day are listed."
        />
      )}
      {groups.map((group) => (
        <Section key={group.userId} aria-label={group.name}>
          <SectionHeader>
            <Typography variant="body1" fontWeight={600}>
              {groupHeading(group)}
            </Typography>
            <Button
              variant="ghost"
              color="tertiary"
              size="sm"
              onClick={() => onOpenMember(group.userId)}
            >
              Open member
            </Button>
          </SectionHeader>
          <ContactList>
            {group.items.map((item) => {
              const href = toContactHistoryHref(item.contact);
              const label = contactLabel(item);
              return (
                <ActivityEntry
                  key={item.entry.id}
                  entry={item.entry}
                  lead={
                    href ? (
                      <TextButton
                        color="neutral"
                        nativeButton={false}
                        render={<RouterLink to={href} />}
                      >
                        {label}
                      </TextButton>
                    ) : (
                      <Typography variant="body3" fontWeight={500} noWrap>
                        {label}
                      </Typography>
                    )
                  }
                />
              );
            })}
          </ContactList>
          {group.deals.length > 0 && (
            <ContactList>
              {group.deals.map((deal) => (
                <DealRow
                  key={`${deal.entryId}-${deal.createdAt ?? ''}-${deal.advancedAt ?? ''}`}
                  deal={deal}
                />
              ))}
            </ContactList>
          )}
        </Section>
      ))}
    </>
  );
};

interface DayDrawerProps {
  day: string | null;
  timeZone: string;
  memberIds: string[] | undefined;
  bounds: { first: string; last: string } | null;
  onChangeDay: (_day: string) => void;
  onOpenMember: (_userId: string) => void;
  onClose: () => void;
}

export const DayDrawer: FC<DayDrawerProps> = ({
  day,
  timeZone,
  memberIds,
  bounds,
  onChangeDay,
  onOpenMember,
  onClose,
}) => {
  const query = useQuery({
    ..._getActivityDay({ date: day ?? '', timeZone, memberIds }),
    enabled: day !== null,
  });

  const previousDay = day ? addIsoDays(day, -1) : null;
  const nextDay = day ? addIsoDays(day, 1) : null;
  const hasPrevious = !!(bounds && previousDay && previousDay >= bounds.first);
  const hasNext = !!(bounds && nextDay && nextDay <= bounds.last);

  return (
    <Drawer
      open={day !== null}
      onOpenChange={(open) => !open && onClose()}
      title={day ? dayTitle(day) : ''}
      maxWidth={DRAWER_WIDTH}
      headerActions={
        <SectionActions>
          <IconButton
            size="sm"
            variant="ghost"
            color="tertiary"
            aria-label="Previous day"
            disabled={!hasPrevious}
            onClick={() => previousDay && onChangeDay(previousDay)}
          >
            <ChevronLeft width={20} height={20} />
          </IconButton>
          <IconButton
            size="sm"
            variant="ghost"
            color="tertiary"
            aria-label="Next day"
            disabled={!hasNext}
            onClick={() => nextDay && onChangeDay(nextDay)}
          >
            <ChevronRight width={20} height={20} />
          </IconButton>
        </SectionActions>
      }
    >
      <DrawerBody>
        {query.isError && !query.data ? (
          <QuietLine role="alert">
            <Typography variant="body2" color={theme.colors.text.secondary}>
              This day couldn’t be loaded.
            </Typography>
            <Button variant="outline" color="tertiary" size="md" onClick={() => query.refetch()}>
              Try again
            </Button>
          </QuietLine>
        ) : !query.data ? (
          <Typography variant="body3" color={theme.colors.text.secondary}>
            Loading…
          </Typography>
        ) : (
          <DayContent data={query.data} onOpenMember={onOpenMember} />
        )}
      </DrawerBody>
    </Drawer>
  );
};
